<template>
  <div class="px-6 border-b border-mid-neutral-50 sticky left-0 top-0 z-[52] bg-white">
    <div class="flex justify-between items-center w-full">
      <div>
        <template v-for="r in routes" :key="'tab-' + r.name">
          <button
            :title="r.tooltip"
            class="p-4 text-sm font-medium relative border-b-2"
            :class="{
              'text-primary-500   border-primary-500': r.name === route.name,
              'text-gray-500 border-transparent': r.name !== route.name,
              '!text-gray-300 border-transparent': r.disabled,
            }"
            :disabled="r.disabled"
            @click="router.push({ name: r.name })"
          >
            {{ r.title }}
          </button>
        </template>
      </div>

      <div class="flex items-center gap-1">
        <div class="flex items-center border border-gray-200 rounded-lg">
          <FloatingMenu placement="bottom" :offset="[0, 8]">
            <template #activator="{ show }">
              <button
                class="p-2 text-gray-600 hover:text-purple-600 hover:bg-gray-50 transition-colors border-r border-gray-200"
                title="AI Assistants Settings"
                @click="show"
              >
                <SparklesIcon class="h-5 w-5" />
              </button>
            </template>
            <template #content>
              <div class="flex flex-col bg-white rounded-lg shadow-lg p-4 gap-3 min-w-64">
                <div class="text-sm font-semibold text-gray-700 mb-1">AI Assistants</div>
                <Switch
                  v-model="autoScreeningEnabled"
                  :disabled="loading.isLoading.value"
                  @update:model-value="handleToggleScreening"
                >
                  <span class="text-sm text-gray-700">AI Screening</span>
                </Switch>
                <Switch
                  v-model="autoExtractionEnabled"
                  :disabled="loading.isLoading.value"
                  @update:model-value="handleToggleExtraction"
                >
                  <span class="text-sm text-gray-700">AI Extraction</span>
                </Switch>
                <Switch
                  v-if="isTextualSynthesisEnabled"
                  v-model="autoSynthesisEnabled"
                  :disabled="loading.isLoading.value"
                  @update:model-value="handleToggleSynthesis"
                >
                  <span class="text-sm text-gray-700">AI Synthesis</span>
                </Switch>
                <Switch
                  v-if="isTextualSynthesisEnabled && hasPreviousVersion"
                  v-model="autoSynthesisDeltaEnabled"
                  :disabled="loading.isLoading.value"
                  @update:model-value="handleToggleSynthesisDelta"
                >
                  <span class="text-sm text-gray-700">AI Changes</span>
                </Switch>
              </div>
            </template>
          </FloatingMenu>

          <JobsStatusModal :review-id="review.entity.value.id" />
        </div>
      </div>
    </div>
  </div>

  <router-view v-slot="{ Component }">
    <keep-alive>
      <component :is="Component" />
    </keep-alive>
  </router-view>
</template>
<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../use-review";

import { SnackbarState } from "@app/types";

import JobsStatusModal from "@app/components/Review/JobsStatusModal.vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import Switch from "@app/components/Global/Switch.vue";
import SparklesIcon from "@app/components/Icons/SparklesIcon.vue";
import { useRoute, useRouter } from "vue-router";
import useSnackbar from "@app/composables/use-snackbar";
import useLoading from "@app/composables/use-loading";

const review = injectStrict(ReviewKey);
const snackbar = useSnackbar();
const loading = useLoading();

const isAutoScreeningPaused = ref(true);
const isAutoExtractionPaused = ref(true);
const isAutoSynthesisPaused = ref(true);
const isAutoSynthesisDeltaPaused = ref(true);

const autoScreeningEnabled = computed(() => !isAutoScreeningPaused.value);

const autoExtractionEnabled = computed(() => !isAutoExtractionPaused.value);

const autoSynthesisEnabled = computed(() => !isAutoSynthesisPaused.value);

const autoSynthesisDeltaEnabled = computed(() => !isAutoSynthesisDeltaPaused.value);

const hasPreviousVersion = computed(() => {
  const siblingReviews = review.entity.value.reviewSeries.reviews.toSorted(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  return siblingReviews[0].id !== review.entity.value.id;
});

const isTextualSynthesisEnabled = computed(() => {
  return review.entity.value.plan?.synthesisPlan?.isTextualSynthesisEnabled ?? false;
});

const route = useRoute();
const router = useRouter();

function fetchPauseState() {
  const plan = review.entity.value.plan as any;
  isAutoScreeningPaused.value = plan?.isAutoScreeningPaused ?? true;
  isAutoExtractionPaused.value = plan?.isAutoExtractionPaused ?? true;
  isAutoSynthesisPaused.value = plan?.isAutoSynthesisPaused ?? true;
  isAutoSynthesisDeltaPaused.value = plan?.isAutoSynthesisDeltaPaused ?? true;
}

async function handleToggleScreening() {
  loading.start();
  try {
    if (isAutoScreeningPaused.value) {
      await review.resumeAutoScreening();
      snackbar.success("Success");
    } else {
      await review.pauseAutoScreening();
      snackbar.success("Success");
    }
    await review.refresh();
    fetchPauseState();
  } catch (error: any) {
    snackbar.show(SnackbarState.ERROR, error.message || "Failed to toggle auto screening");
  } finally {
    loading.stop();
  }
}

async function handleToggleExtraction() {
  loading.start();
  try {
    if (isAutoExtractionPaused.value) {
      await review.resumeAutoExtraction();

      snackbar.success("Success");
    } else {
      await review.pauseAutoExtraction();
      snackbar.success("Success");
    }
    await review.refresh();
    fetchPauseState();
  } catch (error: any) {
    snackbar.show(SnackbarState.ERROR, error.message || "Failed to toggle auto extraction");
  } finally {
    loading.stop();
  }
}

async function handleToggleSynthesis() {
  loading.start();
  try {
    if (isAutoSynthesisPaused.value) {
      await review.resumeAutoSynthesis();

      snackbar.success("Success");
    } else {
      await review.pauseAutoSynthesis();
      snackbar.success("Success");
    }
    review.refresh();
    fetchPauseState();
  } catch (error: any) {
    snackbar.show(SnackbarState.ERROR, error.message || "Failed to toggle auto synthesis");
  } finally {
    loading.stop();
  }
}

async function handleToggleSynthesisDelta() {
  loading.start();
  try {
    if (isAutoSynthesisDeltaPaused.value) {
      await review.resumeAutoSynthesisDelta();
      snackbar.success("Success");
    } else {
      await review.pauseAutoSynthesisDelta();
      snackbar.success("Success");
    }
    review.refresh();
    fetchPauseState();
  } catch (error: any) {
    snackbar.show(SnackbarState.ERROR, error.message || "Failed to toggle auto synthesis delta");
  } finally {
    loading.stop();
  }
}

onMounted(() => {
  fetchPauseState();
});

const routes = computed(() => {
  const rts = [
    {
      name: "Review - Execute - Screening",
      title: "Screening",
    },

    {
      name: "Review - Execute - Appraisal",
      title: "Appraisal",
      disabled:
        !review.entity.value?.plan?.appraisalPlan.isImdrfMdce2019Applicable &&
        !review.entity.value?.plan?.appraisalPlan.isOxfordLevelOfEvidenceApplicable &&
        !review.entity.value?.plan?.appraisalPlan.isPeerReviewStatusApplicable,
      tooltip:
        !review.entity.value?.plan?.appraisalPlan.isImdrfMdce2019Applicable &&
        !review.entity.value?.plan?.appraisalPlan.isOxfordLevelOfEvidenceApplicable &&
        !review.entity.value?.plan?.appraisalPlan.isPeerReviewStatusApplicable
          ? "Enable appraisal in the review plan to access this tab."
          : "",
    },

    {
      name: "Review - Execute - Data Extraction",
      title: "Extraction",
      disabled: review.entity.value?.plan?.synthesisPlan?.attributesStructure.length === 0,
      tooltip:
        review.entity.value?.plan?.synthesisPlan?.attributesStructure.length === 0
          ? "Specify the objective questions in the plan to access this tab."
          : "",
    },

    {
      name: "Review - Execute - Synthesis",
      title: "Synthesis",
      disabled:
        !review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled ||
        review.entity.value?.plan?.synthesisPlan?.attributesStructure.length === 0,
      tooltip: !review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled
        ? "Enable synthesis in the review plan to access this tab."
        : review.entity.value?.plan?.synthesisPlan?.attributesStructure.length === 0
          ? "Specify the objective questions in the plan to access this tab."
          : "",
    },
  ];
  const siblingReviews = review.entity.value.reviewSeries.reviews.toSorted(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const isFirstReview = siblingReviews[0].id === review.entity.value.id;

  rts.push({
    name: "Review - Execute - Synthesis Delta",
    title: "Changes",
    disabled: !review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled || isFirstReview,
    tooltip: !review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled
      ? "Enable synthesis in the review plan to access this tab."
      : isFirstReview
        ? "Changes tab is only available for review updates."
        : "",
  });

  return rts;
});
</script>
